import { CalendarRange, Link2, RadioTower } from "lucide-react";
import type { ReactNode } from "react";

import { useOverview, usePipelineHealth, useTelemetry } from "@/hooks/queries";
import { formatInt, formatPct } from "@/lib/format";

// Persistent data-confidence ribbon under the top bar. Surfaces global TRUST signals straight from
// the data so the whole product reads as honest context — never an error. Neutral styling only
// (no RAG-red): these are coverage/freshness facts, not health states.
//
// Signals:
//   - District match coverage   — matched / (matched+unmatched) recipients (overview + pipeline).
//   - Telemetry freshness        — fresh share vs >2d-stale share (telemetry.staleness).
//   - Data window                — the analysis window the metrics cover (telemetry/overview meta).

interface Signal {
  readonly icon: typeof Link2;
  readonly label: string;
  readonly value: string;
  readonly detail: string;
}

export const DataConfidenceRibbon = (): ReactNode => {
  const overview = useOverview();
  const telemetry = useTelemetry();
  const pipeline = usePipelineHealth();

  const signals: Signal[] = [];

  // 1) District match coverage.
  const kpis = overview.data?.data.kpis;
  const pipelinePct = pipeline.data?.data.accountMatchRatePct ?? null;
  if (kpis) {
    const matched = kpis.districtsMatched;
    const total = kpis.districtsMatched + kpis.districtsUnmatched;
    const pct = pipelinePct ?? (total > 0 ? (matched / total) * 100 : 0);
    signals.push({
      icon: Link2,
      label: "District match coverage",
      value: `${formatInt(matched)} / ${formatInt(total)}`,
      detail: `${formatPct(pct, 0)} of recipients matched to a district_id`,
    });
  }

  // 2) Telemetry freshness.
  const staleness = telemetry.data?.data.staleness;
  if (staleness) {
    const total = staleness.fresh + staleness.recent + staleness.stale;
    const freshPct = total > 0 ? (staleness.fresh / total) * 100 : 0;
    signals.push({
      icon: RadioTower,
      label: "Telemetry freshness",
      value: `${formatPct(freshPct, 0)} fresh`,
      detail: `${formatPct(staleness.stalePct, 0)} of readings are >2d stale`,
    });
  }

  // 3) Data window — prefer telemetry's window, fall back to overview's.
  const window = telemetry.data?.meta.window ?? overview.data?.meta.window ?? null;
  if (window) {
    signals.push({
      icon: CalendarRange,
      label: "Data window",
      value: `${window.start} → ${window.end}`,
      detail: "analysis window the metrics cover",
    });
  }

  if (signals.length === 0) {
    return (
      <section className="confidence-ribbon" aria-label="Data confidence">
        <span className="cr-intro">Data confidence</span>
        <span className="cr-loading">loading trust signals…</span>
      </section>
    );
  }

  return (
    <section className="confidence-ribbon" aria-label="Data confidence">
      <span className="cr-intro">Data confidence</span>
      {signals.map((s) => {
        const Icon = s.icon;
        return (
          <span key={s.label} className="cr-signal" title={s.detail}>
            <Icon size={13} aria-hidden />
            <span className="cr-label">{s.label}</span>
            <span className="cr-value br-tabular-nums">{s.value}</span>
            <span className="cr-detail">{s.detail}</span>
          </span>
        );
      })}
    </section>
  );
};
